<template>
  <div id="app">
    <SiteHeader />
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <SiteFooter :dark-mode="true" />
    
    <!-- Глобальное модальное окно оборудования -->
    <EquipmentDetailModal
      v-if="$store.state.equipmentModal.show"
      :machine="$store.state.equipmentModal.machine"
      :image="$store.state.equipmentModal.image"
      @close="$store.commit('closeEquipmentModal')"
    />
  </div>
</template>

<script>
import SiteHeader from '@/components/Header.vue'
import SiteFooter from '@/components/Footer.vue'
import EquipmentDetailModal from '@/components/EquipmentDetailModal.vue'

export default {
  name: 'App',
  components: {
    SiteHeader,
    SiteFooter,
    EquipmentDetailModal
  }
}
</script>

<style>
:root {
  --primary-color: #0a4d77;
  --primary-light: #1976b2;
  --primary-dark: #003654;
  --secondary-color: #ff6b00;
  --secondary-light: #ff8834;
  --secondary-dark: #e55c00;
  --accent-color: #40a0bf;
  --dark-color: #1a2a36;
  --gray-color: #5e6e7b;
  --light-gray: #e9ecef;
  --light-color: #f8f9fa;
  --white-color: #ffffff;
  --success-color: #1db954;
  --danger-color: #e4405f;
  --warning-color: #ffbc00;
  --info-color: #0dcaf0;
  --box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  --border-radius: 8px;
  --transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

body {
  font-family: 'Montserrat', 'Inter', 'Roboto', 'Segoe UI', Helvetica, Arial, sans-serif;
  font-weight: 200;
  letter-spacing: 0.04em;
  color: var(--dark-color);
  line-height: 1.6;
  font-size: 16px;
  background-color: #f9fafb;
}

h1, h2, h3, h4, h5, h6 {
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 1rem;
  color: var(--dark-color);
}

h1 {
  font-size: 2.5rem;
}

h2 {
  font-size: 2rem;
}

h3 {
  font-size: 1.75rem;
}

h4 {
  font-size: 1.5rem;
}

p {
  margin-bottom: 1rem;
}

a {
  color: var(--primary-color);
  text-decoration: none;
  transition: var(--transition);
}

a:hover {
  color: var(--primary-light);
}

.section {
  padding: 90px 0;
}

.section-title {
  position: relative;
  margin-bottom: 2.5rem;
  font-weight: 700;
}

.section-title:after {
  content: '';
  display: block;
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, var(--secondary-color), var(--secondary-light));
  margin-top: 0.75rem;
  border-radius: 2px;
}

.text-center .section-title:after {
  margin-left: auto;
  margin-right: auto;
}

.bg-primary {
  background-color: var(--primary-color) !important;
}

.bg-secondary {
  background-color: var(--secondary-color) !important;
}

.text-primary {
  color: var(--primary-color) !important;
}

.text-secondary {
  color: var(--secondary-color) !important;
}

.btn {
  padding: 0.75rem 1.75rem;
  font-weight: 600;
  border-radius: var(--border-radius);
  transition: var(--transition);
  border: none;
  cursor: pointer;
  letter-spacing: 0.3px;
  position: relative;
  overflow: hidden;
  z-index: 1;
}

.btn:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 0%;
  height: 100%;
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
  z-index: -1;
}

.btn:hover:before {
  width: 100%;
}

.btn-lg {
  padding: 1rem 2.25rem;
  font-size: 1.1rem;
}

.btn-primary {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
  color: white;
  box-shadow: 0 4px 10px rgba(10, 77, 119, 0.2);
}

.btn-primary:hover, .btn-primary:focus {
  background: linear-gradient(135deg, var(--primary-light), var(--primary-color));
  box-shadow: 0 6px 15px rgba(10, 77, 119, 0.3);
  transform: translateY(-2px);
}

.btn-secondary {
  background: linear-gradient(135deg, var(--secondary-color), var(--secondary-light));
  color: white;
  box-shadow: 0 4px 10px rgba(255, 107, 0, 0.2);
}

.btn-secondary:hover, .btn-secondary:focus {
  background: linear-gradient(135deg, var(--secondary-light), var(--secondary-color));
  box-shadow: 0 6px 15px rgba(255, 107, 0, 0.3);
  transform: translateY(-2px);
}

.btn-outline-primary {
  border: 2px solid var(--primary-color);
  color: var(--primary-color);
  background: transparent;
  font-weight: 600;
}

.btn-outline-primary:hover, .btn-outline-primary:focus {
  background-color: var(--primary-color);
  color: white;
  box-shadow: 0 4px 10px rgba(10, 77, 119, 0.2);
  transform: translateY(-2px);
}

.btn-outline-secondary {
  border: 2px solid var(--secondary-color);
  color: var(--secondary-color);
  background: transparent;
  font-weight: 600;
}

.btn-outline-secondary:hover, .btn-outline-secondary:focus {
  background-color: var(--secondary-color);
  color: white;
  box-shadow: 0 4px 10px rgba(255, 107, 0, 0.2);
  transform: translateY(-2px);
}

.btn-outline-dark {
  border: 2px solid var(--dark-color);
  color: var(--dark-color);
  background: transparent;
  font-weight: 600;
}

.btn-outline-dark:hover, .btn-outline-dark:focus {
  background-color: var(--dark-color);
  color: white;
  box-shadow: 0 4px 10px rgba(26, 42, 54, 0.2);
  transform: translateY(-2px);
}

.card {
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--box-shadow);
  transition: var(--transition);
  border: none;
  background-color: var(--white-color);
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
}

* {
  font-family: 'Montserrat', 'Inter', 'Roboto', 'Segoe UI', Helvetica, Arial, sans-serif !important;
  letter-spacing: 0.04em !important;
}

:where(h1, h2, h3, h4, h5, h6, p, a, span, div, button, input, textarea) {
  font-family: 'Montserrat', 'Inter', 'Roboto', 'Segoe UI', Helvetica, Arial, sans-serif !important;
}

.fa, .fas, .far, .fal, .fab, .fad, .fa-solid, .fa-regular, .fa-light, .fa-duotone, .fa-brands {
  font-family: 'Font Awesome 6 Free', 'Font Awesome 6 Pro', 'FontAwesome', 'Font Awesome 5 Free', 'Font Awesome 5 Pro', 'FontAwesome', sans-serif !important;
  font-weight: 900 !important;
  letter-spacing: normal !important;
}

/* Fade transition for router views */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
